import React, { useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { FileText, Download, TestTube, Bug } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { DocmosisService } from '@/services/docmosisService';

function FileNamingTest() {
  const { toast } = useToast();
  const [jobDescription, setJobDescription] = useState('');
  const [extractedData, setExtractedData] = useState({
    company: '',
    jobTitle: '',
    filename: ''
  });
  const [testResume, setTestResume] = useState(`John Smith
Software Engineer
john.smith@email.com | (555) 123-4567

PROFESSIONAL SUMMARY
Experienced software engineer with 5+ years developing web applications using React, Node.js, and modern technologies.

TECHNICAL SKILLS
• JavaScript, TypeScript, React, Node.js
• Python, SQL, MongoDB, PostgreSQL
• AWS, Docker, Git, CI/CD

PROFESSIONAL EXPERIENCE
Senior Software Engineer | TechCorp Inc. | 2021 - Present
• Developed and maintained web applications serving 100K+ users
• Led team of 3 developers on key product features
• Improved application performance by 40% through optimization

Software Engineer | StartupXYZ | 2019 - 2021  
• Built React frontend applications from scratch
• Implemented REST APIs using Node.js and Express
• Collaborated with design team on user experience improvements`);
  
  const [sampleJobDescriptions] = useState([
    {
      name: "Google Software Engineer",
      content: `Company: Google
Position: Senior Software Engineer
Location: Mountain View, CA

We are looking for a Senior Software Engineer to join our team. You will be responsible for designing and developing scalable web applications.

Requirements:
- 5+ years of software development experience
- Proficiency in JavaScript, Python, or Java
- Experience with cloud platforms (GCP preferred)
- Strong problem-solving skills

About Google:
Google's mission is to organize the world's information and make it universally accessible and useful.`
    },
    {
      name: "Microsoft Product Manager",
      content: `Microsoft is seeking a Product Manager for our Azure division.

Job Title: Senior Product Manager - Azure
Company: Microsoft Corporation

Key Responsibilities:
- Define product strategy and roadmap
- Work with engineering teams to deliver features
- Analyze market trends and user feedback

Qualifications:
- MBA or equivalent experience
- 7+ years in product management
- Experience with cloud technologies`
    },
    {
      name: "Startup Format",
      content: `TechStartup Inc. seeks a Full Stack Developer

We're a fast-growing startup looking for a talented Full Stack Developer to join our team.

Position: Full Stack Developer
Organization: TechStartup Inc.
Location: Remote

What you'll do:
- Build our core product using React and Node.js
- Work directly with founders on product strategy
- Scale our platform to handle growing user base`
    },
    {
      name: "Complex Format",
      content: `SENIOR DATA SCIENTIST POSITION

Amazon Web Services (AWS) Machine Learning Team

Amazon is hiring a Senior Data Scientist for the AWS ML team. This role involves developing cutting-edge machine learning models and algorithms.

Location: Seattle, WA
Team: Machine Learning Platform
Department: Amazon Web Services

We are looking for someone who can:
- Design and implement ML algorithms
- Work with large datasets
- Collaborate with product teams`
    }
  ]);

  // Enhanced company extraction with multiple patterns
  const extractCompanyFromJobData = (jobDesc: string) => {
    if (!jobDesc) return '';
    
    const lines = jobDesc.split('\n');
    
    // Try multiple extraction patterns
    for (const line of lines) {
      const lowerLine = line.toLowerCase();
      
      // Pattern 1: "Company: XYZ"
      if (lowerLine.startsWith('company:')) {
        return line.replace(/^company:\s*/i, '').trim();
      }
      
      // Pattern 2: "Organization: XYZ" 
      if (lowerLine.startsWith('organization:')) {
        return line.replace(/^organization:\s*/i, '').trim();
      }
      
      // Pattern 3: "Employer: XYZ"
      if (lowerLine.startsWith('employer:')) {
        return line.replace(/^employer:\s*/i, '').trim();
      }
      
      // Pattern 4: "At XYZ Company" or "at XYZ"
      if (lowerLine.includes(' at ') && line.length < 100) {
        const match = line.match(/\bat\s+([^,\n]+)/i);
        if (match) return match[1].trim();
      }
      
      // Pattern 5: "XYZ is seeking" or "XYZ seeks"
      if ((lowerLine.includes(' is seeking') || lowerLine.includes(' seeks')) && line.length < 100) {
        const match = line.match(/^([^,\n]+?)\s+(?:is seeking|seeks)/i);
        if (match) return match[1].trim();
      }
    }
    
    // Pattern 6: Look for company names in first few lines (common job posting format)
    for (let i = 0; i < Math.min(5, lines.length); i++) {
      const line = lines[i].trim();
      if (line && !line.toLowerCase().includes('job') && !line.toLowerCase().includes('position') 
          && line.length > 2 && line.length < 80 && !line.includes('@')) {
        // Skip if it looks like a job title or email
        const lowerLine = line.toLowerCase();
        if (!lowerLine.includes('engineer') && !lowerLine.includes('manager') && 
            !lowerLine.includes('director') && !lowerLine.includes('specialist') &&
            !lowerLine.includes('analyst') && !lowerLine.includes('developer')) {
          return line;
        }
      }
    }
    
    return '';
  };

  // Enhanced job title extraction with fallback logic
  const extractJobTitleFromDescription = (jobDesc: string) => {
    if (!jobDesc) return '';
    
    const lines = jobDesc.split('\n');
    
    // Try multiple extraction patterns
    for (const line of lines) {
      const lowerLine = line.toLowerCase();
      const trimmedLine = line.trim();
      
      // Pattern 1: "Job Title: XYZ" or "Position: XYZ"
      if (lowerLine.startsWith('job title:') || lowerLine.startsWith('position:') || lowerLine.startsWith('role:')) {
        return trimmedLine.replace(/^(job title|position|role):\s*/i, '').trim();
      }
      
      // Pattern 2: Look for job-related keywords in first few lines
      if (trimmedLine && trimmedLine.length < 100) {
        const jobKeywords = ['engineer', 'manager', 'director', 'specialist', 'analyst', 'developer', 
                           'coordinator', 'supervisor', 'lead', 'senior', 'junior', 'associate', 'assistant'];
        if (jobKeywords.some(keyword => lowerLine.includes(keyword))) {
          return trimmedLine;
        }
      }
    }
    
    // Pattern 3: First non-empty line if it looks like a job title
    const firstLine = lines.find(line => line.trim())?.trim() || '';
    if (firstLine && firstLine.length < 100 && !firstLine.includes('@') && !firstLine.toLowerCase().includes('company')) {
      return firstLine;
    }
    
    return '';
  };

  const testExtraction = () => {
    const company = extractCompanyFromJobData(jobDescription);
    const jobTitle = extractJobTitleFromDescription(jobDescription);
    
    // Generate filename like the actual system does
    const currentDate = new Date().toLocaleString('en-US', { 
      month: 'short', 
      year: 'numeric' 
    });
    
    const baseFilename = 'John Smith Resume';
    const jobTitlePart = jobTitle ? ` - ${jobTitle}` : '';
    const companyPart = company ? ` - ${company}` : '';
    const filename = `${baseFilename}${jobTitlePart}${companyPart} - ${currentDate}.docx`;
    
    setExtractedData({
      company: company || 'Target Company',
      jobTitle: jobTitle || 'Target Position', 
      filename
    });

    console.log('=== EXTRACTION TEST RESULTS ===');
    console.log('Job Description:', jobDescription);
    console.log('Extracted Company:', company);
    console.log('Extracted Job Title:', jobTitle);
    console.log('Generated Filename:', filename);
  };

  const testPDFConversion = async () => {
    try {
      // Test the convert-word-to-pdf function with a sample document
      const { data, error } = await supabase.functions.invoke('convert-word-to-pdf', {
        body: {
          fileUrl: 'https://gxatnnzwaggcvzzurgsq.supabase.co/storage/v1/object/public/word-documents/sample-resume.docx',
          filename: 'test-resume.docx'
        }
      });

      if (error) {
        console.error('PDF conversion error:', error);
        toast({
          title: "PDF conversion failed",
          description: error.message,
          variant: "destructive"
        });
      } else {
        console.log('PDF conversion success:', data);
        toast({
          title: "PDF conversion successful",
          description: "Check console for details"
        });
      }
    } catch (error) {
      console.error('PDF conversion exception:', error);
      toast({
        title: "PDF conversion error",
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: "destructive"
      });
    }
  };

  const testDocmosisExport = async () => {
    try {
      const documentData = {
        id: 'test-1',
        type: 'resume',
        title: 'Test Resume',
        content: testResume,
        jobTitle: extractedData.jobTitle !== 'Target Position' ? extractedData.jobTitle : 'Software Engineer',
        company: extractedData.company !== 'Target Company' ? extractedData.company : 'Google'
      };

      const blob = await DocmosisService.exportToWord([documentData]);
      
      // Create proper filename with name, job title, company and date
      const currentDate = new Date().toLocaleString('en-US', { 
        month: 'short', 
        year: 'numeric' 
      });
      const jobTitle = documentData.jobTitle ? ` - ${documentData.jobTitle}` : '';
      const company = documentData.company ? ` - ${documentData.company}` : '';
      const filename = `John Smith${jobTitle}${company} - ${currentDate}.docx`;
      
      DocmosisService.downloadBlob(blob, filename);
      
      toast({
        title: "Export successful",
        description: "Document downloaded successfully"
      });
    } catch (error) {
      console.error('Docmosis export error:', error);
      toast({
        title: "Export failed",
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: "destructive"
      });
    }
  };

  return (
    <AppLayout>
      <div className="space-y-8">
        <PageHeader
          title="File Naming & PDF Test"
          description="Test file naming extraction and PDF conversion functionality"
        />

        {/* File Naming Tests */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5" />
              File Naming Extraction Test
            </CardTitle>
            <CardDescription>
              Test the extraction of company names and job titles from job descriptions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Sample Job Descriptions */}
            <div>
              <Label className="text-sm font-medium">Sample Job Descriptions</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {sampleJobDescriptions.map((sample, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => setJobDescription(sample.content)}
                    className="justify-start h-auto p-3"
                  >
                    <div className="text-left">
                      <div className="font-medium">{sample.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {sample.content.substring(0, 60)}...
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            {/* Job Description Input */}
            <div>
              <Label htmlFor="job-desc">Job Description</Label>
              <Textarea
                id="job-desc"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste a job description here..."
                rows={8}
                className="mt-2"
              />
            </div>

            {/* Test Button */}
            <Button onClick={testExtraction} disabled={!jobDescription.trim()}>
              <Bug className="w-4 h-4 mr-2" />
              Test Extraction
            </Button>

            {/* Results */}
            {extractedData.company || extractedData.jobTitle ? (
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium">Extraction Results:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Company Name</Label>
                    <div className="mt-1">
                      <Badge variant={extractedData.company === 'Target Company' ? 'destructive' : 'default'}>
                        {extractedData.company}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Job Title</Label>
                    <div className="mt-1">
                      <Badge variant={extractedData.jobTitle === 'Target Position' ? 'destructive' : 'default'}>
                        {extractedData.jobTitle}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Generated Filename</Label>
                  <div className="mt-1 p-2 bg-background rounded border">
                    <code className="text-sm">{extractedData.filename}</code>
                  </div>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Separator />

        {/* PDF & Export Tests */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Document Export Tests
            </CardTitle>
            <CardDescription>
              Test PDF conversion and Word document generation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Test Resume */}
            <div>
              <Label htmlFor="test-resume">Test Resume Content</Label>
              <Textarea
                id="test-resume"
                value={testResume}
                onChange={(e) => setTestResume(e.target.value)}
                rows={8}
                className="mt-2 font-mono text-sm"
              />
            </div>

            {/* Test Buttons */}
            <div className="flex gap-4">
              <Button onClick={testDocmosisExport}>
                <Download className="w-4 h-4 mr-2" />
                Test Word Export
              </Button>
              
              <Button onClick={testPDFConversion} variant="outline">
                <FileText className="w-4 h-4 mr-2" />
                Test PDF Conversion
              </Button>
            </div>

            {/* Instructions */}
            <div className="text-sm text-muted-foreground space-y-2">
              <p><strong>Word Export Test:</strong> Downloads a Word document using the current extraction results</p>
              <p><strong>PDF Conversion Test:</strong> Tests the convert-word-to-pdf edge function (check console for logs)</p>
              <p><strong>Debug:</strong> Open browser console (F12) to see detailed logs</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

export default FileNamingTest;